import React, { useLayoutEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { ScoreIdsInput } from "./ReconciliationModal/ScoreIdsInput";
import { CampaignPOInput } from "./ReconciliationModal/CampaignPOInput";
import { BusinessUnitInput } from "./ReconciliationModal/BusinessUnitInput";
import { Summary } from "./ReconciliationModal/Summary";
import { ProjectMinimumInput } from "./ReconciliationModal/ProjectMinimumInput";
import { FinanceNoteInput } from "./ReconciliationModal/FinanceNoteInput";
import { SubmitHandler, useForm } from "react-hook-form";
import { useDebounce } from "use-debounce";

import flow from "lodash/fp/flow";
import uniq from "lodash/fp/uniq";
import filter from "lodash/fp/filter";
import partition from "lodash/fp/partition";

export interface CustomerBillingAddress {
  id: string;
  name: string;
}

export interface ReconciliationFormData {
  accepted_ids: string;
  po: string;
  customer_billing_address_id: string;
  charge_project_minimum: boolean;
  finance_note: string;
}

export interface ScoreIdWithCPI {
  identifier: string;
  adjusted_cpi?: string;
  cpi?: string;
}

export type ScoresByCPI = Record<string, ScoreIdWithCPI[]>;

export interface ReconciliationModalProps {
  onSubmit?: SubmitHandler<ReconciliationFormData>;
  customerBillingAddresses?: CustomerBillingAddress[];
  customerBillingAddressId?: string;
  playerScoreIdsByCPI: ScoresByCPI;
  completedPlayerScoreIds: string[];
  idKey?: string;
  po: string;
  currentCPI: number;
  chargeProjectMinimum?: boolean;
  externalCustomer?: boolean;
}

const filterIds = (
  rawAcceptedIds: (string | undefined)[],
  allIdsByIdentifier: Record<string, ScoreIdWithCPI>,
  completedByIdentifier: Record<string, ScoreIdWithCPI>
) => {
  const rejectedById = { ...completedByIdentifier };
  let acceptedTotal = 0.0;
  let avgCPI = 0.0;
  const acceptedIdsByCPI: ScoresByCPI = {};
  const [acceptedIds, unrecognizedIds] = flow(
    filter((id: string) => id && id !== "" && id !== "\n"),
    uniq,
    partition((id: string) => {
      const acceptedIdentifier = allIdsByIdentifier[id];
      if (!!acceptedIdentifier) {
        delete rejectedById[id];
        acceptedTotal += parseFloat(acceptedIdentifier.cpi);
        acceptedIdsByCPI[acceptedIdentifier.cpi] = [
          ...(acceptedIdsByCPI[acceptedIdentifier.cpi] || []),
          acceptedIdentifier,
        ];
      }

      return !!acceptedIdentifier;
    })
  )(rawAcceptedIds || []);

  avgCPI = acceptedTotal / acceptedIds.length;

  return {
    acceptedIds,
    avgCPI,
    unrecognizedIds,
    rejectedById,
    acceptedTotal,
    acceptedIdsByCPI,
  };
};
export const ReconciliationModal = ({
  currentCPI,
  customerBillingAddresses,
  customerBillingAddressId,
  po,
  playerScoreIdsByCPI,
  completedPlayerScoreIds,
  idKey,
  chargeProjectMinimum,
  externalCustomer = false,
  onSubmit,
}: ReconciliationModalProps) => {
  const { register, watch, handleSubmit } = useForm();

  const rawAcceptedIdsString = watch("accepted_ids", "");
  const [acceptedIdsString] = useDebounce(
    rawAcceptedIdsString,
    completedPlayerScoreIds.length > 10000 ? 500 : 10
  );

  const { idsByIdentifier } = React.useMemo(
    () =>
      Object.keys(playerScoreIdsByCPI).reduce<{
        idsByIdentifier: Record<string, ScoreIdWithCPI>;
      }>(
        (acc, key) => {
          const playerScoreInCPI = playerScoreIdsByCPI[key];
          playerScoreInCPI.forEach((playerScore) => {
            acc.idsByIdentifier[playerScore.identifier] = {
              ...playerScore,
              cpi: key,
            };
          });
          return acc;
        },
        { idsByIdentifier: {} }
      ),
    [playerScoreIdsByCPI]
  );

  const { completedById: completedByIdentifier } = React.useMemo(() => {
    return completedPlayerScoreIds.reduce<{
      completeIdsByCPI: ScoresByCPI;
      completedById: Record<string, ScoreIdWithCPI>;
    }>(
      (acc, id) => {
        const { cpi } = idsByIdentifier[id];
        acc.completeIdsByCPI[cpi] = [
          ...(acc.completeIdsByCPI[cpi] || []),
          idsByIdentifier[id],
        ];
        acc.completedById[id] = idsByIdentifier[id];
        return acc;
      },
      { completeIdsByCPI: {}, completedById: {} }
    );
  }, [completedPlayerScoreIds, idsByIdentifier]);

  const {
    acceptedIds,
    unrecognizedIds,
    rejectedById,
    acceptedTotal,
    acceptedIdsByCPI,
    avgCPI,
  } = React.useMemo(
    () =>
      filterIds(
        acceptedIdsString === ""
          ? []
          : acceptedIdsString.split(/(?:[,| :;(\n)]| )+/),
        idsByIdentifier,
        completedByIdentifier
      ),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [acceptedIdsString]
  );

  const { rejectedByCPI, rejectedTotal, rejectedIds } = React.useMemo(() => {
    const rejectedIds = Object.keys(rejectedById);

    const { rejectedByCPI, rejectedTotal } = rejectedIds.reduce<{
      rejectedByCPI: Record<string, ScoreIdWithCPI[]>;
      rejectedTotal: number;
    }>(
      (acc, id) => {
        const record = rejectedById[id];
        acc.rejectedByCPI[record.cpi] = [
          ...(acc.rejectedByCPI[record.cpi] || []),
          record,
        ];
        acc.rejectedTotal += parseFloat(record.cpi);
        return acc;
      },
      { rejectedByCPI: {}, rejectedTotal: 0.0 }
    );

    return { rejectedByCPI, rejectedTotal, rejectedIds };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptedIdsString]);

  // It isn't worth it doing this in React right now
  useLayoutEffect(() => {
    if (!externalCustomer) {
      if (acceptedTotal < window.Constants.ADHOC_PROJECT_MINIMUM) {
        window.$(".project-minimum").slideDown();
      } else {
        window.$(".project-minimum").slideUp();
      }
    }
  }, [acceptedTotal, externalCustomer]);

  const innerOnSubmit: SubmitHandler<ReconciliationFormData> = (data) => {
    onSubmit({ ...data, accepted_ids: acceptedIds.join(",") });
  };

  const buildPercentageString = (value:number, total:number) => {
    const percent = total === 0 ? 0 : ((value * 100 / total).toFixed(2));
    return ` (${percent}%)`
  };

  const totalPc = "";
  const acceptedPc = buildPercentageString(acceptedIds.length, completedPlayerScoreIds.length)
  const rejectedPc = buildPercentageString(rejectedIds.length, completedPlayerScoreIds.length)
  const unrecognizedPc = buildPercentageString(unrecognizedIds.length, completedPlayerScoreIds.length)

  return (
    <form
      id="campaign-reconciliation-form"
      onSubmit={handleSubmit(innerOnSubmit)}
    >
      <Row>
        <Col sm={4}>
          <ScoreIdsInput
            title="Recorded IDs"
            metricsClassName="recorded-metrics"
            values={completedPlayerScoreIds}
            testidPrefix="recorded-ids"
            percent={totalPc}
          />
        </Col>

        <Col sm={4}>
          <ScoreIdsInput
            title="Accepted IDs"
            metricsClassName="accepted-metrics"
            placeholder="Enter accepted IDs"
            testidPrefix="accepted-ids"
            values={acceptedIds}
            percent={acceptedPc}
            {...register("accepted_ids")}
          />
          {idKey && (<div>For this campaign, the IDs are associated with the variable <b>{idKey}</b> from your entry URL</div>)}
        </Col>

        <Col sm={4}>
          <CampaignPOInput po={po} {...register("po")} />
          {(customerBillingAddresses?.length > 1 ||
            customerBillingAddressId) && (
            <>
              <br />
              <br />
              <BusinessUnitInput
                businessAddresses={customerBillingAddresses}
                selectedBusinessId={customerBillingAddressId}
                {...register("customer_billing_address_id")}
              />
              <br />
              <br />
            </>
          )}
          <FinanceNoteInput {...register("finance_note")} />
        </Col>
      </Row>

      <Row>
        <Col sm={4}>
          <ScoreIdsInput
            title="Rejected IDs (verify with client)"
            metricsClassName="rejected-metrics"
            testidPrefix="rejected-ids"
            values={rejectedIds}
            percent={rejectedPc}
          />
        </Col>
        <Col sm={4}>
          <ScoreIdsInput
            title="Unrecognized IDs"
            metricsClassName="incorrectly-accepted-metrics"
            testidPrefix="unrecognized-ids"
            values={unrecognizedIds}
            percent={unrecognizedPc}
          />
        </Col>

        <Col sm={4} className="align-center spacing-top-3x">
          <Summary
            cpi={currentCPI}
            avgCPI={avgCPI}
            acceptedTotal={acceptedTotal}
            rejectedTotal={rejectedTotal}
            acceptedIdsByCPI={acceptedIdsByCPI}
            rejectedIdsByCPI={rejectedByCPI}
          />
          {!externalCustomer && (
            <ProjectMinimumInput
              checked={chargeProjectMinimum}
              {...register("charge_project_minimum")}
            />
          )}

          <button type="submit" className="btn complete-reconciliation">
            Complete reconciliation
          </button>
        </Col>
      </Row>
    </form>
  );
};
